import { atomWithStorage, useHydrateAtoms } from 'jotai/utils';

import { createCookieValueStorage } from '@/utils/cookie-storage';

export type BillingMode = 'monthly' | 'yearly';

export const BILLING_COOKIE_NAME = 'uppbeat_billing_mode';

/**
 * Type guard to check if a string is a valid BillingMode.
 */
export function isBillingMode(value: unknown): value is BillingMode {
  return (
    typeof value === 'string' && (value === 'monthly' || value === 'yearly')
  );
}

// Create cookie storage instance using the utility
const billingModeCookieStorage = createCookieValueStorage<BillingMode>(
  isBillingMode,
  'yearly',
  {
    expires: 30, // 30 days
  }
);

/**
 * Store the selected billing mode in cookies
 * Default to yearly as it was in the UserStateProvider
 *
 * This atom should not be used directly - use the useBillingMode hook instead
 */
export const selectedBillingModeAtom = atomWithStorage<BillingMode>(
  BILLING_COOKIE_NAME,
  'yearly',
  billingModeCookieStorage
);

selectedBillingModeAtom.debugLabel = 'selectedBillingModeAtom';

/**
 * Custom hook for hydrating billing mode atom
 */
export const useHydrateBillingMode = (initialMode?: BillingMode) => {
  useHydrateAtoms([
    [
      selectedBillingModeAtom,
      initialMode && isBillingMode(initialMode) ? initialMode : 'yearly',
    ],
  ] as const);
};

/**
 * Tracks if the subscription update notification has been dismissed by the user.
 * - `false` (default): The notification has not been dismissed and can be shown.
 * - `true`: The notification has been dismissed and should be hidden.
 *
 * Used to control the visibility of information about new subscription types
 * for users on legacy plans (e.g., Premium, Business).
 */
export const subscriptionUpdateDismissedAtom = atomWithStorage(
  'subscriptionUpdate', // New storage key
  false // Default to false (not dismissed)
);

subscriptionUpdateDismissedAtom.debugLabel = 'subscriptionUpdateDismissedAtom';
